import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";

export interface CampaignApprovalData {
  id: number;
  approval_status?: "pending" | "approved" | "rejected";
  approval_requested_at?: string | null;
  approval_decided_at?: string | null;
  approval_decided_by_name?: string | null;
  approval_notes?: string | null;
  can_approve?: boolean;
  owner_display_name: string;
  submitted_by?: {
    is_email_verified: boolean;
    identity_status: string;
    member_since: string;
    other_campaigns: number;
  } | null;
}

interface Props {
  campaign: CampaignApprovalData;
  onChanged: () => void | Promise<void>;
}

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

const errorText = (err: unknown, fallback: string) => {
  const data = (
    err as { response?: { data?: { detail?: string; error?: string } } }
  )?.response?.data;
  return data?.detail ?? data?.error ?? fallback;
};

const IDENTITY_LABELS: Record<string, string> = {
  approved: "ID verified",
  pending: "ID under review",
  rejected: "ID rejected",
  not_submitted: "No ID submitted",
};

const CampaignApprovalBanner = ({ campaign, onChanged }: Props) => {
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const status = campaign.approval_status;
  if (!status || status === "approved") return null;

  const isPending = status === "pending";
  const s = campaign.submitted_by;

  const approve = async () => {
    setBusy("approve");
    try {
      await apiService.post(`/api/chema/campaigns/${campaign.id}/approve/`, {});
      toast.success("Campaign approved. The owner has been notified.");
      await onChanged();
    } catch (err) {
      toast.error(errorText(err, "Could not approve this campaign."));
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    setBusy("reject");
    try {
      await apiService.post(`/api/chema/campaigns/${campaign.id}/reject/`, {
        notes: notes.trim(),
      });
      toast.success("Campaign sent back to the owner.");
      setRejectOpen(false);
      setNotes("");
      await onChanged();
    } catch (err) {
      toast.error(errorText(err, "Could not reject this campaign."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          mb: 3,
          borderLeft: "4px solid",
          borderColor: isPending ? "#f59e0b" : "#ef4444",
          bgcolor: isPending ? "#fffbeb" : "#fef2f2",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 240, flex: 1 }}>
            <Typography fontWeight={700}>
              {isPending ? "Awaiting your approval" : "Rejected"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isPending
                ? `Submitted by ${campaign.owner_display_name}${
                    campaign.approval_requested_at
                      ? ` on ${formatDate(campaign.approval_requested_at)}`
                      : ""
                  }. It stays hidden from the public until it is approved.`
                : `${
                    campaign.approval_decided_by_name
                      ? `By ${campaign.approval_decided_by_name}`
                      : "Sent back"
                  }${
                    campaign.approval_decided_at
                      ? ` on ${formatDate(campaign.approval_decided_at)}`
                      : ""
                  }${campaign.approval_notes ? ` — “${campaign.approval_notes}”` : ""}`}
            </Typography>
          </Box>

          {campaign.can_approve && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {isPending && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={busy !== null}
                  onClick={() => setRejectOpen(true)}
                  sx={{ textTransform: "none", borderRadius: 3 }}
                >
                  Reject
                </Button>
              )}
              <Button
                variant="contained"
                disabled={busy !== null}
                onClick={approve}
                startIcon={
                  busy === "approve" ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  bgcolor: "#10b981",
                  "&:hover": { bgcolor: "#059669" },
                }}
              >
                Approve
              </Button>
            </Box>
          )}
        </Box>

        {s && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
            <Chip
              size="small"
              label={s.is_email_verified ? "Email verified" : "Email not verified"}
              color={s.is_email_verified ? "success" : "warning"}
              variant="outlined"
            />
            <Chip
              size="small"
              label={IDENTITY_LABELS[s.identity_status] ?? s.identity_status}
              color={s.identity_status === "approved" ? "success" : "default"}
              variant="outlined"
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Member since ${formatDate(s.member_since)}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${s.other_campaigns} other campaign${s.other_campaigns === 1 ? "" : "s"}`}
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={rejectOpen}
        onClose={() => busy === null && setRejectOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send this campaign back?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The owner will see your reason and can update and resubmit.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            placeholder="e.g. Please add a clearer description and a photo."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectOpen(false)}
            disabled={busy !== null}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={reject}
            disabled={busy !== null || !notes.trim()}
            sx={{ textTransform: "none" }}
          >
            {busy === "reject" ? "Sending…" : "Send back"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignApprovalBanner;
